<script setup lang="ts">
import { onMounted, ref, computed, nextTick, onBeforeUnmount } from 'vue'
import { useThrottleFn } from '@vueuse/core'
import type { IMercariProductListItemClickEvent } from '@/components/MercariProductList.vue'
import { useMercariFilter } from '@/lib/useMercariFilter'
import { getSortingMethodLabel } from '@/lib/mercariFilterConfig'
import MercariProductListingSection from '@/components/MercariProductListingSection.vue'
import type { IMercariProductListItemContent } from '@/components/MercariProductListItem.vue'
import PageBreadCrumb from '@/components/PageBreadCrumb.vue'
import type { IBreadcrumbItem } from '@/types/BreadCrumb'
import { getMercariUrl } from '@/lib/mercari'
import getRedirectSeoLink from '@/lib/getSeoRedirect'
import {
	createGAEvent,
	GaGeneralEvent,
	GaGeneralPageName,
} from '@/assets/ts/customEvent'
import { getSiteBaseUrl } from '@/lib/siteBaseUrl'
import {
	fetchMercariCollectionDetail,
	type IMercariBrand,
	type IMercariCollectionDetail,
} from '@/assets/ts/apiManagement'
import { useBreakpoints } from '@/lib/utils'
import BaseIcon from '@/components/ui/icon/BaseIcon.vue'

const { isLarge } = useBreakpoints()

const props = defineProps<{
	pageContent: {
		breadcrumbs: {
			home: string
			shopping: string
			mercari: string
		}
		resultsTitle: string
		productItem: IMercariProductListItemContent
		productListLoadMoreButtonText: string
		edgeCase: {
			empty: {
				description: string
			}
			serverError: {
				title: string
				description: string
				buttonText: string
			}
		}
		filterContent: any
		description: {
			viewMore: string
			viewLess: string
		}
	}
}>()

/**
 * Gets the brand and collection slugs from URL
 * - In local environment: reads from query parameters
 * - In production: extracts from URL path (e.g., /mercari/brands/pokemon/white-flare → brand: pokemon, collection: white-flare)
 */
const getSlugsFromUrl = (): { brandSlug: string; collectionSlug: string } => {
	if (import.meta.env.PROD) {
		const path = window.location.pathname
		const segments = path.split('/').filter(Boolean)
		// Expected path: /shopping/marketplaces/mercari/brands/{brand}/{collection}/
		const brandsIndex = segments.indexOf('brands')
		if (brandsIndex !== -1 && segments.length > brandsIndex + 2) {
			return {
				brandSlug: segments[brandsIndex + 1] || '',
				collectionSlug: segments[brandsIndex + 2] || '',
			}
		}
		return { brandSlug: '', collectionSlug: '' }
	}
	const params = new URLSearchParams(window.location.search)
	return {
		brandSlug: params.get('brand_slug') || '',
		collectionSlug: params.get('collection_slug') || '',
	}
}

const slugs = getSlugsFromUrl()
const brandSlug = ref(slugs.brandSlug)
const collectionSlug = ref(slugs.collectionSlug)
const brandData = ref<IMercariBrand | null>(null)
const collectionData = ref<IMercariCollectionDetail | null>(null)
const searchKeyword = ref('')
const isLoading = ref(true)
const hasError = ref(false)
const isDescriptionExpanded = ref(false)
const shouldShowDescriptionToggle = ref(false)
const isDescriptionReady = ref(false)
const descriptionRef = ref<HTMLParagraphElement | null>(null)

const productSectionRef =
	ref<InstanceType<typeof MercariProductListingSection>>()

const DESCRIPTION_CLAMP_CLASSES = ['line-clamp-2', 'lg:line-clamp-3']

/**
 * Checks if description text would be truncated when line-clamped.
 * If currently expanded, temporarily applies clamp classes to measure.
 */
const checkDescriptionTruncation = async () => {
	const element = descriptionRef.value
	if (!element) {
		isDescriptionReady.value = true
		return
	}

	const wasExpanded = isDescriptionExpanded.value

	// Temporarily apply line-clamp to measure truncation
	if (wasExpanded) {
		DESCRIPTION_CLAMP_CLASSES.forEach(cls => element.classList.add(cls))
		await nextTick()
	}

	const wouldBeTruncated = element.scrollHeight > element.clientHeight

	// Restore expanded state
	if (wasExpanded) {
		DESCRIPTION_CLAMP_CLASSES.forEach(cls => element.classList.remove(cls))
	}

	shouldShowDescriptionToggle.value = wouldBeTruncated
	isDescriptionReady.value = true
}

const throttledCheckDescriptionTruncation = useThrottleFn(
	checkDescriptionTruncation,
	200,
	true, // trailing: true - ensures final call after resize ends
)

const productListRef = computed(() => productSectionRef.value?.productListRef)

const mercariFilter = useMercariFilter({
	productListRef,
	locationId: GaGeneralPageName.MercariCollectionListing,
	filterContent: props.pageContent.filterContent,
	keyword: searchKeyword.value,
})

const isTranslated = ref(true)

const brandName = computed(() => brandData.value?.name || '')
const collectionName = computed(() => collectionData.value?.name || '')
const collectionDescription = computed(
	() => collectionData.value?.description || '',
)

const hasDescription = computed(() => !!collectionDescription.value)

const breadcrumbs = computed<IBreadcrumbItem[]>(() => [
	{
		displayName: props.pageContent.breadcrumbs.home,
		categoryId: 'home',
		link: getSiteBaseUrl(),
	},
	{
		displayName: props.pageContent.breadcrumbs.shopping,
		categoryId: 'shopping',
		link: `${getSiteBaseUrl()}/${getRedirectSeoLink('proxyShopping')}/`,
	},
	{
		displayName: props.pageContent.breadcrumbs.mercari,
		categoryId: 'mercari',
		link: getMercariUrl('index'),
	},
	{
		displayName: brandName.value,
		categoryId: 'brand',
		link: getMercariUrl('brand-listing', { brand_slug: brandSlug.value }),
	},
	{
		displayName: collectionName.value,
		categoryId: 'collection',
		link: '',
	},
])

const toggleDescriptionExpanded = () => {
	isDescriptionExpanded.value = !isDescriptionExpanded.value
}

const fetchCollectionData = async () => {
	if (!brandSlug.value || !collectionSlug.value) {
		hasError.value = true
		isLoading.value = false

		window.location.href = getMercariUrl('index')
		return
	}

	try {
		isLoading.value = true
		hasError.value = false

		const data = await fetchMercariCollectionDetail(
			brandSlug.value,
			collectionSlug.value,
		)

		brandData.value = data.brand
		collectionData.value = data.collection

		searchKeyword.value = data.collection.search_query || ''
	} catch (error) {
		console.error('Failed to fetch collection data:', error)
		hasError.value = true
	} finally {
		isLoading.value = false
	}
}

const handleItemClick = (data: IMercariProductListItemClickEvent) => {
	const filterValues = mercariFilter.getFilterTrackingValues()

	const categoryValues = mercariFilter.categoryLevels?.value
		? mercariFilter.createCategoryValuesFromLevels(
				mercariFilter.categoryLevels.value,
		  )
		: {}

	const sortingMethodLabel = getSortingMethodLabel(
		mercariFilter.filterState.sort,
	)

	createGAEvent('select_item', {
		page_name: GaGeneralPageName.MercariCollectionListing,
		event_extra: `${brandData.value?.brand_id}_${collectionData.value?.collection_id}`,
		sorting_method: sortingMethodLabel,
		item_category: categoryValues.item_category || '',
		item_category2: categoryValues.item_category2 || '',
		item_category3: categoryValues.item_category3 || '',
		item_category4: categoryValues.item_category4 || '',
		filter_condition: filterValues.filter_condition || '',
		filter_availability: filterValues.filter_availability || '',
		filter_price: filterValues.filter_price || '',
		filter_lister: filterValues.filter_lister || '',
		items: [
			{
				location_id: GaGeneralPageName.MercariIndex,
				item_list_name: 'Product Listing',
				item_id: `product_${data.item.id}`,
				item_name: data.item.name,
				item_category: 'Mercari',
				index: data.index + 1,
			},
		],
	})

	window.open(data.targetUrl, '_blank')
}

onMounted(async () => {
	await fetchCollectionData()

	await mercariFilter.initialize()

	// Check truncation after content is rendered
	await nextTick()
	checkDescriptionTruncation()

	window.addEventListener('resize', throttledCheckDescriptionTruncation)

	createGAEvent(GaGeneralEvent.PageLoad, {
		page_name: GaGeneralPageName.MercariCollectionListing,
		event_extra: `${brandData.value?.brand_id}_${collectionData.value?.collection_id}`,
	})
})

onBeforeUnmount(() => {
	window.removeEventListener('resize', throttledCheckDescriptionTruncation)
})
</script>

<template>
	<section class="bs-container">
		<div class="col-span-12 pb-[37px] mdp:pb-[60px] lg:mt-32px">
			<div v-if="!isLoading" class="flex flex-col gap-16px lg:gap-24px">
				<!-- Breadcrumb -->
				<PageBreadCrumb
					:breadcrumbs="breadcrumbs"
					:is-override-seo-check="true"
					class="p-0"
					is-last-item-self
				/>

				<!-- Collection Description Section -->
				<div
					class="flex flex-col gap-8px rounded-lg bg-o-75 p-16px lg:gap-16px lg:p-24px"
					:class="{ invisible: !isDescriptionReady }"
				>
					<!-- Collection title -->
					<h1
						class="text-black"
						:class="isLarge ? 'typography-h4-str' : 'typography-h5-str'"
					>
						{{ collectionName }}
					</h1>

					<!-- Description with view more/less -->
					<div v-if="hasDescription" class="flex flex-col gap-4px">
						<p
							ref="descriptionRef"
							class="whitespace-pre-line text-n-800"
							:class="[
								isLarge ? 'typography-s1-reg' : 'typography-s2-reg',
								{
									'line-clamp-2 lg:line-clamp-3': !isDescriptionExpanded,
								},
							]"
							v-html="collectionDescription"
						></p>

						<!-- View more/less button (only show if text should be truncated) -->
						<button
							v-if="shouldShowDescriptionToggle"
							@click="toggleDescriptionExpanded"
							class="flex w-fit items-center gap-4px text-n-950"
						>
							<span class="typography-l2-emp">
								{{
									isDescriptionExpanded
										? pageContent.description.viewLess
										: pageContent.description.viewMore
								}}
							</span>
							<BaseIcon
								name="chevron_thin"
								class="h-16px w-16px fill-o-500 transition-transform"
								:class="{
									'rotate-[-90deg]': isDescriptionExpanded,
									'rotate-90': !isDescriptionExpanded,
								}"
							/>
						</button>
					</div>
				</div>

				<!-- Main content -->
				<main>
					<MercariProductListingSection
						ref="productSectionRef"
						:key="searchKeyword"
						:filter-instance="mercariFilter"
						:filter-content="props.pageContent.filterContent"
						:keyword="searchKeyword"
						shop-id=""
						:item-content="props.pageContent.productItem"
						:load-more-button-text="
							props.pageContent.productListLoadMoreButtonText
						"
						:edge-case="props.pageContent.edgeCase"
						:is-translated="isTranslated"
						:location-id="GaGeneralPageName.MercariCollectionListing"
						:hide-category-filter="true"
						@item-click="handleItemClick"
					>
						<template #search-result-title>
							<div
								class="hidden items-center justify-center mdp:flex mdp:justify-start"
							>
								<span class="typography-s1-str text-n-500">
									{{ pageContent.resultsTitle }}
								</span>
							</div>
						</template>
					</MercariProductListingSection>
				</main>
			</div>
		</div>
	</section>
</template>
